/**
 * @file Button.jsx
 * @description Reusable button component with shadcn-style variants.
 * @module components/ui/Button
 */
import React from "react";

/* ══════════════════════════════════════════════════════════════
 * STYLES & CONFIG
 * ══════════════════════════════════════════════════════════════ */
const VARIANT_CLASSES = {
  default:
    "bg-emerald-600 text-primary-foreground shadow-sm hover:bg-emerald-700 active:bg-emerald-800",
  secondary:
    " text-secondary-foreground shadow-sm hover:bg-slate-200 active:bg-slate-300",
  outline:
    "bg-white border-2 border-emerald-600 bg-transparent text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground",
  ghost:
    "text-foreground hover:bg-accent hover:text-accent-foreground",
  link:
    "text-primary underline-offset-4 hover:underline",
  destructive:
    "bg-destructive text-white shadow-sm hover:bg-red-600 active:bg-red-700",
  login:
    "text-gray-300 shadow-sm hover:text-white"
};

const SIZE_CLASSES = {
  sm:      "h-8 px-3 text-sm rounded gap-1.5",
  default: "h-10 px-5 text-sm rounded gap-2",
  lg:      "h-10 px-6 sm:h-12 sm:px-8 text-sm sm:text-base rounded gap-2 sm:gap-2.5",
  icon:    "h-10 w-10 rounded flex items-center justify-center",
  login:   "h-12 text-base rounded gap-2.5",
};

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component Button
 * @description Reusable UI button component.
 *
 * @param {Object} props
 * @param {"default"|"secondary"|"outline"|"ghost"|"link"|"destructive"|"login"} [props.variant="default"] - The visual style variant of the button.
 * @param {"sm"|"default"|"lg"|"icon"|"login"} [props.size="default"] - The size variant of the button.
 * @param {string} [props.className] - Additional CSS classes.
 * @param {React.ReactNode} props.children - The content to be rendered inside the button.
 * @returns {JSX.Element} The rendered Button component.
 */
const Button = ({
  variant = "default",
  size = "default",
  className = "",
  children,
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center font-medium transition-colors duration-200 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 " +
    "disabled:pointer-events-none disabled:opacity-50 cursor-pointer";

  const variantClasses = VARIANT_CLASSES[variant] || VARIANT_CLASSES.default;
  const sizeClasses = SIZE_CLASSES[size] || SIZE_CLASSES.default;

  return (
    <button
      className={`${baseClasses} ${variantClasses} ${sizeClasses} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;
